import { useEffect, useMemo, useRef, useState } from 'react';
import { getLotsByChantier } from '../lib/lotService';
import { getTasksByChantier } from '../lib/taskService';
import { buildLotSegments, derniereFinExclusive, finInclusive } from '../lib/lotSegments';
import './ChantierTimelineModal.css';

const RULER_HEIGHT = 26;
const ROW_HEIGHT = 32;
const ROW_GAP = 8;
const MAX_VISIBLE_ROWS = 20;
const MAX_ROWS_HEIGHT = MAX_VISIBLE_ROWS * (ROW_HEIGHT + ROW_GAP) - ROW_GAP;

const LABEL_MIN_WIDTH = 64;
const NUMERO_MIN_WIDTH = 30;
const MIN_BAR_WIDTH = 18;
const DAY_MS = 86_400_000;

const MIN_MONTH_LABEL_GAP = 58;

const ZOOM_MIN = 3;
const ZOOM_MAX = 24;
const ZOOM_STEP = 3;

const TERMINE_COLOR = '#10B981';
const A_VENIR_COLOR = '#F59E0B';

const STATUS_FILTERS = [
  ['en_cours', 'En cours', null],
  ['a_venir', 'À venir', A_VENIR_COLOR],
  ['termine', 'Terminé', TERMINE_COLOR],
];

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, days) {
  return new Date(date.getTime() + days * DAY_MS);
}

function daysBetween(a, b) {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
}

function pxPerDayFor(totalDays) {
  if (totalDays <= 30) return 14;
  if (totalDays <= 60) return 10;
  if (totalDays <= 120) return 7;
  return 4;
}

function statusOf(lot, today) {
  if (lot.avancement === 'termine') return 'termine';
  return startOfDay(new Date(lot.date_debut_prevue)) > today ? 'a_venir' : 'en_cours';
}

function statusColor(status, themeColor) {
  if (status === 'termine') return TERMINE_COLOR;
  if (status === 'a_venir') return A_VENIR_COLOR;
  return themeColor;
}

function formatSegment(segment) {
  const options = { day: '2-digit', month: 'short' };
  return `${new Date(segment.debut).toLocaleDateString('fr-FR', options)} → ${new Date(
    finInclusive(segment)
  ).toLocaleDateString('fr-FR', options)}`;
}

// Un lot séquentiel (interrompu puis repris) s'affiche en plusieurs barres reliées par un trait fin.
function LotsTimeline({ lots, taches, themeColor }) {
  const [selectedStatuses, setSelectedStatuses] = useState(() => new Set(['en_cours', 'a_venir']));
  const [zoomPxPerDay, setZoomPxPerDay] = useState(null);
  const [selectedLotId, setSelectedLotId] = useState(null);
  const scrollRef = useRef(null);

  const datedLots = useMemo(
    () =>
      lots
        .filter((l) => !!l.date_debut_prevue && l.duree_jours !== null && l.duree_jours !== undefined)
        .sort((a, b) => a.date_debut_prevue.localeCompare(b.date_debut_prevue)),
    [lots]
  );
  const unscheduledLots = useMemo(
    () => lots.filter((l) => !(l.date_debut_prevue && l.duree_jours !== null && l.duree_jours !== undefined)),
    [lots]
  );
  const segmentsByLot = useMemo(
    () => new Map(datedLots.map((lot) => [lot.id, buildLotSegments(lot, taches ?? []) ?? []])),
    [datedLots, taches]
  );

  const layout = useMemo(() => {
    if (datedLots.length === 0) return null;

    const today = startOfDay(new Date());
    let rangeStart = startOfDay(new Date(datedLots[0].date_debut_prevue));
    let rangeEnd = rangeStart;
    for (const lot of datedLots) {
      const start = startOfDay(new Date(lot.date_debut_prevue));
      const segments = segmentsByLot.get(lot.id) ?? [];
      const end =
        segments.length > 0
          ? startOfDay(new Date(derniereFinExclusive(segments)))
          : addDays(start, Math.max(lot.duree_jours, 1));
      if (start < rangeStart) rangeStart = start;
      if (end > rangeEnd) rangeEnd = end;
    }
    rangeStart = addDays(rangeStart < today ? rangeStart : today, -3);
    rangeEnd = addDays(rangeEnd > today ? rangeEnd : today, 3);

    const totalDays = Math.max(daysBetween(rangeStart, rangeEnd), 14);
    const autoPxPerDay = pxPerDayFor(totalDays);
    const pxPerDay = zoomPxPerDay ?? autoPxPerDay;
    const width = totalDays * pxPerDay;

    const weekTicks = [];
    for (let d = 0; d <= totalDays; d += 7) weekTicks.push(d * pxPerDay);

    const monthMarkers = [];
    const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    let lastMarkerX = -Infinity;
    while (cursor <= rangeEnd) {
      const x = Math.max(0, daysBetween(rangeStart, cursor)) * pxPerDay;
      if (x - lastMarkerX >= MIN_MONTH_LABEL_GAP) {
        const label = cursor.toLocaleDateString('fr-FR', {
          month: 'short',
          year: cursor.getFullYear() !== today.getFullYear() ? '2-digit' : undefined,
        });
        monthMarkers.push({ x, label });
        lastMarkerX = x;
      }
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const todayX = today >= rangeStart && today <= rangeEnd ? daysBetween(rangeStart, today) * pxPerDay : null;
    const initialOffsetX = todayX !== null ? Math.max(0, todayX - 60) : 0;

    return { today, rangeStart, rangeEnd, pxPerDay, width, weekTicks, monthMarkers, todayX, initialOffsetX };
  }, [datedLots, segmentsByLot, zoomPxPerDay]);

  const statusCounts = useMemo(() => {
    const counts = { en_cours: 0, a_venir: 0, termine: 0 };
    if (!layout) return counts;
    for (const lot of datedLots) counts[statusOf(lot, layout.today)]++;
    return counts;
  }, [datedLots, layout]);

  const rows = useMemo(() => {
    if (!layout) return [];
    return datedLots
      .map((lot) => ({ lot, status: statusOf(lot, layout.today) }))
      .filter(({ status }) => selectedStatuses.has(status))
      .map(({ lot, status }) => {
        const segments = segmentsByLot.get(lot.id) ?? [];
        const bars = segments.map((segment) => {
          const start = startOfDay(new Date(segment.debut));
          const end = startOfDay(new Date(segment.finExclusive));
          const x = daysBetween(layout.rangeStart, start) * layout.pxPerDay;
          const width = Math.max(daysBetween(start, end) * layout.pxPerDay, MIN_BAR_WIDTH);
          return { x, width };
        });
        const links = bars.slice(1).map((bar, i) => {
          const left = bars[i].x + bars[i].width;
          return { left, width: Math.max(bar.x - left, 0) };
        });
        return { lot, segments, bars, links, status, color: statusColor(status, themeColor) };
      });
  }, [datedLots, segmentsByLot, layout, selectedStatuses, themeColor]);

  // Recentre sur "aujourd'hui" au chargement et à chaque changement de zoom.
  useEffect(() => {
    if (layout && scrollRef.current) {
      scrollRef.current.scrollLeft = layout.initialOffsetX;
    }
  }, [zoomPxPerDay, layout]);

  useEffect(() => {
    if (selectedLotId !== null && !rows.some((r) => r.lot.id === selectedLotId)) {
      setSelectedLotId(null);
    }
  }, [rows, selectedLotId]);

  if (!layout) {
    return (
      <div className="LotsTimeline-emptyState">
        <p className="LotsTimeline-emptyText">Aucune donnée de planning disponible pour l'instant.</p>
      </div>
    );
  }

  function toggleStatus(status) {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }

  const rowsContentHeight = rows.length > 0 ? rows.length * (ROW_HEIGHT + ROW_GAP) - ROW_GAP : 0;
  const rowsBoxHeight = Math.min(rowsContentHeight, MAX_ROWS_HEIGHT);
  const selectedLot = rows.find((r) => r.lot.id === selectedLotId) ?? null;
  const canZoomIn = layout.pxPerDay < ZOOM_MAX;
  const canZoomOut = layout.pxPerDay > ZOOM_MIN;

  return (
    <div>
      <div className="LotsTimeline-controlsRow">
        <div className="LotsTimeline-filterChips">
          {STATUS_FILTERS.map(([status, label, staticColor]) => {
            const color = staticColor || themeColor;
            const active = selectedStatuses.has(status);
            return (
              <button
                key={status}
                type="button"
                className="LotsTimeline-filterChip"
                style={{
                  backgroundColor: active ? `${color}18` : '#2c2d2e',
                  borderColor: active ? color : 'transparent',
                  color: active ? color : '#bbbbbb',
                }}
                onClick={() => toggleStatus(status)}
              >
                {label} · {statusCounts[status]}
              </button>
            );
          })}
        </div>

        <div className="LotsTimeline-zoomGroup">
          <button
            type="button"
            className="LotsTimeline-zoomButton"
            disabled={!canZoomOut}
            aria-label="Zoom arrière"
            onClick={() => setZoomPxPerDay(Math.max(ZOOM_MIN, layout.pxPerDay - ZOOM_STEP))}
          >
            −
          </button>
          <button
            type="button"
            className="LotsTimeline-zoomButton"
            disabled={!canZoomIn}
            aria-label="Zoom avant"
            onClick={() => setZoomPxPerDay(Math.min(ZOOM_MAX, layout.pxPerDay + ZOOM_STEP))}
          >
            +
          </button>
        </div>
      </div>

      <div className="LotsTimeline-scroll" ref={scrollRef}>
        <div style={{ width: layout.width, position: 'relative' }}>
          <div className="LotsTimeline-ruler" style={{ height: RULER_HEIGHT }}>
            {layout.weekTicks.map((x, i) => (
              <div key={`w-${i}`} className="LotsTimeline-gridLineRuler" style={{ left: x, height: RULER_HEIGHT }} />
            ))}
            {layout.monthMarkers.map((m, i) => (
              <div key={`m-${i}`} className="LotsTimeline-monthMarker" style={{ left: m.x }}>
                <div className="LotsTimeline-monthTick" />
                <span className="LotsTimeline-monthLabel">{m.label}</span>
              </div>
            ))}
            {layout.todayX !== null && (
              <div
                className="LotsTimeline-todayTick"
                style={{ left: layout.todayX, backgroundColor: themeColor }}
              />
            )}
          </div>

          {rows.length === 0 ? (
            <div className="LotsTimeline-filterEmptyState">
              <p className="LotsTimeline-emptyText">Aucun lot pour ces filtres.</p>
            </div>
          ) : (
            <div className="LotsTimeline-rowsScroll" style={{ height: rowsBoxHeight }}>
              <div style={{ width: layout.width, height: rowsContentHeight, position: 'relative' }}>
                {layout.weekTicks.map((x, i) => (
                  <div
                    key={`wr-${i}`}
                    className="LotsTimeline-gridLine"
                    style={{ left: x, height: rowsContentHeight }}
                  />
                ))}
                {layout.todayX !== null && (
                  <div
                    className="LotsTimeline-todayLine"
                    style={{ left: layout.todayX, height: rowsContentHeight, backgroundColor: themeColor }}
                  />
                )}

                {rows.map(({ lot, bars, links, color }, index) => {
                  const selected = lot.id === selectedLotId;
                  return (
                    <button
                      key={lot.id}
                      type="button"
                      className="LotsTimeline-barButton"
                      style={{ top: index * (ROW_HEIGHT + ROW_GAP), height: ROW_HEIGHT }}
                      onClick={() => setSelectedLotId((prev) => (prev === lot.id ? null : lot.id))}
                    >
                      {links.map((link, linkIndex) => (
                        <div
                          key={`link-${linkIndex}`}
                          className="LotsTimeline-link"
                          style={{ left: link.left, width: link.width, backgroundColor: color }}
                        />
                      ))}
                      {bars.map((bar, barIndex) => {
                        const showFullLabel = bar.width >= LABEL_MIN_WIDTH;
                        const showNumeroOnly = !showFullLabel && bar.width >= NUMERO_MIN_WIDTH;
                        return (
                          <div
                            key={`bar-${barIndex}`}
                            className="LotsTimeline-bar"
                            style={{
                              left: bar.x,
                              width: bar.width,
                              height: ROW_HEIGHT,
                              backgroundColor: color,
                              borderWidth: selected ? 2 : 0,
                            }}
                          >
                            {showFullLabel && (
                              <span className="LotsTimeline-barLabel">
                                {lot.numero} · {lot.nom}
                              </span>
                            )}
                            {showNumeroOnly && <span className="LotsTimeline-barLabel">{lot.numero}</span>}
                          </div>
                        );
                      })}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedLot && (
        <div className="LotsTimeline-detailPanel">
          <div className="LotsTimeline-detailDot" style={{ backgroundColor: selectedLot.color }} />
          <div className="LotsTimeline-detailInfo">
            <p className="LotsTimeline-detailTitle">
              {selectedLot.lot.numero} · {selectedLot.lot.nom}
            </p>
            <p className="LotsTimeline-detailMeta">{selectedLot.segments.map(formatSegment).join('  ·  ')}</p>
          </div>
          <button
            type="button"
            className="LotsTimeline-detailClose"
            aria-label="Fermer le détail"
            onClick={() => setSelectedLotId(null)}
          >
            ×
          </button>
        </div>
      )}

      {unscheduledLots.length > 0 && (
        <div className="LotsTimeline-unscheduledWrap">
          <p className="LotsTimeline-unscheduledLabel">Lots non planifiés</p>
          <div className="LotsTimeline-unscheduledChips">
            {unscheduledLots.map((lot) => (
              <div key={lot.id} className="LotsTimeline-unscheduledChip">
                {lot.numero} · {lot.nom}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Port web de la section "Avancement par lot" de app/(modals)/chantiers/[id]/tasks.tsx
// (app mobile) — mêmes règles de calcul (tâches groupées par lot) et même
// format de carte, pour rester cohérent entre les deux apps.
function LotsProgress({ lots, tasks, themeColor }) {
  const lotProgress = useMemo(() => {
    const sorted = [...lots].sort((a, b) => a.numero - b.numero);
    return sorted.map((lot) => {
      const lotTasks = tasks.filter((task) => task.lot_id === lot.id);
      const total = lotTasks.length;
      const completedCount = lotTasks.filter((task) => task.status === 'completed').length;
      const metier = lot.lot_metiers?.[0]?.metier;

      return {
        lot,
        total,
        completedCount,
        percent: total > 0 ? Math.round((completedCount / total) * 100) : 0,
        color: metier?.color || themeColor,
      };
    });
  }, [lots, tasks, themeColor]);

  return (
    <div className="LotsProgress-section">
      <div className="LotsProgress-header">
        <span className="LotsProgress-title">Avancement par lot</span>
        <span className="LotsProgress-count">{lotProgress.length}</span>
      </div>

      <div className="LotsProgress-list">
        {lotProgress.length === 0 ? (
          <div className="LotsProgress-empty">
            <p className="LotsProgress-emptyTitle">Aucun lot</p>
            <p className="LotsProgress-emptyText">Les lots du chantier apparaîtront ici</p>
          </div>
        ) : (
          lotProgress.map((lp) => {
            const isComplete = lp.total > 0 && lp.completedCount === lp.total;
            const barColor = isComplete ? '#10B981' : lp.color;

            return (
              <div key={lp.lot.id} className="LotsProgress-card">
                <div className="LotsProgress-cardHeader">
                  <div className="LotsProgress-dot" style={{ backgroundColor: lp.color }} />
                  <div className="LotsProgress-info">
                    <p className="LotsProgress-name">
                      {lp.lot.numero} · {lp.lot.nom}
                    </p>
                    <p className="LotsProgress-meta">
                      {lp.total === 0 ? 'Aucune tâche assignée' : `${lp.completedCount}/${lp.total} tâches terminées`}
                    </p>
                  </div>
                  {isComplete ? (
                    <span className="LotsProgress-check">✓</span>
                  ) : (
                    lp.total > 0 && (
                      <span className="LotsProgress-percent" style={{ color: lp.color }}>
                        {lp.percent}%
                      </span>
                    )
                  )}
                </div>

                <div className="LotsProgress-track">
                  {lp.total > 0 && (
                    <div
                      className="LotsProgress-fill"
                      style={{ width: `${lp.percent}%`, backgroundColor: barColor }}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function ChantierTimelineModal({ chantier, onClose }) {
  const [lots, setLots] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const themeColor = chantier?.chantier_type_data?.color || '#F6A500';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([getLotsByChantier(chantier.id), getTasksByChantier(chantier.id)])
      .then(([lotsData, tasksData]) => {
        if (!cancelled) {
          setLots(lotsData);
          setTasks(tasksData);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [chantier.id]);

  return (
    <div className="ModalOverlay" onClick={onClose}>
      <div className="Modal TimelineModal" onClick={(e) => e.stopPropagation()}>
        <div className="Modal-header">
          <div>
            <h2 className="Modal-title">{chantier.name}</h2>
            <p className="Modal-subtitle">Timeline des lots</p>
          </div>
          <button className="Modal-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>

        <div className="Modal-body">
          {loading && <p className="App-loading">Chargement de la timeline…</p>}
          {error && <p className="Modal-error">Impossible de charger la timeline : {error}</p>}
          {!loading && !error && (
            <>
              <LotsTimeline lots={lots} taches={tasks} themeColor={themeColor} />
              <LotsProgress lots={lots} tasks={tasks} themeColor={themeColor} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChantierTimelineModal;
